import { db, getLastSyncTime, setLastSyncTime } from './db';
import axios from 'axios';
import { StorageService } from './StorageService';
import { MAX_LIST_LIMIT } from '../utils/apiConstants';

const API_URL = process.env.REACT_APP_API_URL || '';

const MAX_SYNC_RETRIES = 5;

// Sync mutex: prevents concurrent sync() calls from duplicating server records
let _syncInProgress = null;
let _syncAgain = false;

// 429 backoff: when rate-limited, block all syncs until the cooldown expires
let _rateLimitedUntil = 0;
const RATE_LIMIT_COOLDOWN_MS = 60 * 1000; // wait 60 s before retrying after a 429

// Static data cache: strains/locus/allele change infrequently — pull at most once per 5 minutes
let _lastStaticPull = 0;
const STATIC_DATA_TTL_MS = 5 * 60 * 1000; // 5 minutes

// --- Sync paging (C4) ---
// A pull used to request one page of SYNC_PAGE_SIZE rows per table and stop.
// Anything past that page was silently missing from the local cache while
// lastSync still advanced (the fetch had "succeeded"), so those rows were never
// re-requested — a local-first UI showing a quietly incomplete colony. This hits
// the INITIAL sync too, so a lab with more than one page of active mice was
// incomplete on first login, not merely after a long offline stretch.
const SYNC_PAGE_SIZE = 2000;
// Safety valve against an unbounded loop if the server ever returns a full page
// forever (a cursor that fails to advance). 25 pages = 50 000 rows per table,
// far past any real colony.
const SYNC_MAX_PAGES = 25;

// Helper to get token
const getToken = () => StorageService.get('token');

// Helper to check online status
const isOnline = () => navigator.onLine;

// Compute expected pup genotypes from parent loci using a Punnett square.
// Returns an array of { locus_name, allele_name } objects (same shape as pup_records genotype_data).
// If the cross produces multiple possible outcomes (heterozygous × heterozygous), allele_name is
// left blank (user fills it in) — identical to the webapp's getUniqueParentLoci behaviour.
const computePupGenotype = (motherGenotype, fatherGenotype) => {
    const uniqueLoci = new Set();
    [...(motherGenotype || []), ...(fatherGenotype || [])].forEach(g => g.locus_name && uniqueLoci.add(g.locus_name));

    const alleleSort = (a, b) => {
        if (a === b) return 0;
        if (a === '+') return 1;
        if (b === '+') return -1;
        return a.localeCompare(b);
    };
    const parseAllele = (s) => {
        if (!s) return ['+', '+'];
        if (!s.includes('/')) return null; // BAC / non-diploid format
        const parts = s.split('/').map(p => p.trim());
        return parts.length === 2 ? parts : null;
    };

    return Array.from(uniqueLoci).map(locusName => {
        const mObj = (motherGenotype || []).find(g => g.locus_name === locusName);
        const fObj = (fatherGenotype || []).find(g => g.locus_name === locusName);
        const g1 = parseAllele(mObj?.allele_name);
        const g2 = parseAllele(fObj?.allele_name);
        let inheritedAllele = '';
        if (g1 && g2) {
            const outcomes = [];
            for (const a of g1) for (const b of g2) outcomes.push([a, b].sort(alleleSort).join('/'));
            if (outcomes.every(o => o === outcomes[0])) inheritedAllele = outcomes[0];
        }
        return { locus_name: locusName, allele_name: inheritedAllele };
    });
};

export const DataService = {
  // --- Sync Operations ---

  async sync({ forceStaticRefresh = false } = {}) {
    if (!isOnline()) return { status: 'offline' };
    const token = getToken();
    if (!token) return { status: 'auth_error' };

    // If we were recently rate-limited, skip this sync entirely
    if (Date.now() < _rateLimitedUntil) {
      return { status: 'rate_limited' };
    }

    // If a sync is already running, flag that we need another pass and
    // return the existing promise. This prevents concurrent pushChanges()
    // calls from reading the same queue items and creating duplicates.
    if (_syncInProgress) {
      _syncAgain = true;
      return _syncInProgress;
    }

    const doSync = async () => {
      try {
        await this.pushChanges(token);
        if (forceStaticRefresh) _lastStaticPull = 0;
        await this.pullChanges(token);
        window.dispatchEvent(new CustomEvent('squeakbase-sync-complete'));
        return { status: 'success', timestamp: new Date().toISOString() };
      } catch (error) {
        if (error?.response?.status === 429) {
          _rateLimitedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
          _syncAgain = false; // abort the retry loop — don't hammer the server
          console.warn('Sync rate-limited. Pausing sync for 60 seconds.');
          return { status: 'rate_limited' };
        }
        console.error('Sync failed:', error);
        return { status: 'error', error: error.message };
      }
    };

    try {
      _syncInProgress = doSync();
      let result = await _syncInProgress;

      // If any save/archive queued items while we were syncing,
      // do one more pass to pick them up.
      while (_syncAgain) {
        _syncAgain = false;
        if (Date.now() >= _rateLimitedUntil) {
          _syncInProgress = doSync();
          result = await _syncInProgress;
        }
      }

      return result;
    } finally {
      _syncInProgress = null;
    }
  },

  async pushChanges(token) {
    const queue = await db.syncQueue.toArray();
    if (queue.length === 0) return;

    // Use an index loop so we can patch later items in-place when a CREATE
    // resolves a tempId to a real server ID (e.g. WEAN queued after an
    // offline-created litter needs the real litter ID in its endpoint).
    for (let qi = 0; qi < queue.length; qi++) {
      const item = queue[qi];
      try {
        const headers = { Authorization: `Bearer ${token}` };

        if (item.action === 'CREATE') {
          // Strip the temp ID before sending to backend
          const { id: _tempId, ...dataToSend } = item.payload;
          const response = await axios.post(`${API_URL}${item.endpoint}`, dataToSend, { headers });

          if (item.tempId) {
            const table = item.table || 'mice';
            await db[table].delete(item.tempId);

            // Propagate real server ID to any later queue items that still
            // reference this tempId (covers WEAN endpoints, payload cage_ids, etc.)
            const realId = response.data?.id ?? response.data?.data?.id;
            if (realId) {
              const tempStr = String(item.tempId);
              const realStr = String(realId);
              for (let fi = qi + 1; fi < queue.length; fi++) {
                const dep = queue[fi];
                const updates = {};
                if (dep.endpoint?.includes(tempStr)) {
                  updates.endpoint = dep.endpoint.replace(tempStr, realStr);
                }
                if (dep.payload) {
                  const ps = JSON.stringify(dep.payload);
                  if (ps.includes(tempStr)) {
                    updates.payload = JSON.parse(ps.replaceAll(tempStr, realStr));
                  }
                }
                if (Object.keys(updates).length > 0) {
                  queue[fi] = { ...dep, ...updates };
                  await db.syncQueue.update(dep.id, updates);
                }
              }
            }
          }

        } else if (item.action === 'UPDATE') {
          // If the entity ID is still a temp ID, this UPDATE was queued before its
          // CREATE resolved (e.g., user edited a just-weaned pup while offline).
          // The CREATE is either still pending or failed permanently — skip this UPDATE.
          // If CREATE later succeeds, pullChanges will bring the real record from the server.
          // Check the failedSync table if CREATE failures need investigation.
          const entityId = item.endpoint.split('/').pop();
          if (entityId && String(entityId).startsWith('temp_')) {
            console.warn(`[pushChanges] Dropping UPDATE for still-unresolved temp record: ${entityId}. Endpoint: ${item.endpoint}. Check failedSync table for a CREATE failure.`);
            await db.syncQueue.delete(item.id);
            continue;
          }
          const { id: _id, ...payload } = item.payload;
          await axios.put(`${API_URL}${item.endpoint}`, payload, { headers });

        } else if (item.action === 'DELETE') {
          await axios.delete(`${API_URL}${item.endpoint}`, { headers });

        } else if (item.action === 'BULK_MOVE') {
          await axios.put(`${API_URL}${item.endpoint}`, item.payload, { headers });

        } else if (item.action === 'WEAN') {
          // If still a temp ID the CREATE action failed/hasn't run — skip
          const litterId = item.endpoint.split('/')[3]; // /api/litters/{id}/wean-pups
          if (litterId && String(litterId).startsWith('temp_')) {
            console.warn(`Skipping WEAN for unsynced temp litter: ${litterId}`);
            await db.syncQueue.delete(item.id);
            continue;
          }
          await axios.post(`${API_URL}${item.endpoint}`, item.payload, { headers });
          // Delete locally-created temp mice; pullChanges will bring back server records
          if (item.tempMiceIds && item.tempMiceIds.length > 0) {
            await Promise.all(item.tempMiceIds.map(id => db.mice.delete(id)));
          }
        }

        // Remove from queue on success
        await db.syncQueue.delete(item.id);

      } catch (error) {
        // Propagate rate-limit to the global cooldown
        if (error?.response?.status === 429) {
          _rateLimitedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
          throw error;
        }
        console.error(`Failed to sync queue item ${item.id} (${item.action} ${item.endpoint}):`, error);
        const retryCount = (item.retryCount || 0) + 1;
        if (retryCount >= MAX_SYNC_RETRIES) {
          console.error(`Moving sync queue item ${item.id} to failedSync after ${MAX_SYNC_RETRIES} failed attempts`);
          const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
          await db.failedSync.add({
            action: item.action,
            endpoint: item.endpoint,
            table: item.table || 'mice',
            payload: item.payload,
            tempId: item.tempId || null,
            tempMiceIds: item.tempMiceIds || null,
            timestamp: item.timestamp,
            failedAt: Date.now(),
            lastError: errorMessage,
          });
          await db.syncQueue.delete(item.id);
          // Keep the temp record in its entity table so the user still sees it
        } else {
          await db.syncQueue.update(item.id, { retryCount });
        }
      }
    }
  },

  // Pull one table to completion, following the keyset cursor the backend
  // exposes for sync_mode (C4). Returns false when the table could NOT be
  // drained, so the caller knows not to advance lastSync.
  //
  // Ordering is fixed server-side to (updated_at ASC, id ASC) whenever
  // sync_mode=true, and the cursor is a strict `>` against that pair — so no
  // row is skipped and none is fetched twice. bulkPut is an idempotent upsert
  // regardless, so a repeated row is harmless.
  async _drainTable({ endpoint, table, headers, baseParams }) {
    let cursor = null;

    for (let page = 0; page < SYNC_MAX_PAGES; page++) {
      const params = { ...baseParams, sync_mode: 'true', _limit: SYNC_PAGE_SIZE };
      if (cursor) {
        params.cursor_updated_at = cursor.updated_at;
        params.cursor_id = cursor.id;
      }

      const res = await axios.get(`${API_URL}${endpoint}`, { headers, params });
      // mice/cages/litters answer { data: [...] }; pregnancies answers a bare
      // array. Tolerating both keeps the pregnancy route's existing response
      // shape, which its UI callers depend on.
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);

      const last = rows[rows.length - 1];

      if (rows.length > 0) {
        // sync_cursor is paging bookkeeping, not record data — strip it so the
        // cached rows match what the rest of the app expects.
        await db[table].bulkPut(rows.map(({ sync_cursor, ...record }) => record));
      }

      // A short page means the server had nothing more to give.
      if (rows.length < SYNC_PAGE_SIZE) return true;

      // Prefer the server's sync_cursor string: updated_at arrives as a JS Date
      // parsed in local time (the column is `timestamp without time zone`), so
      // echoing it back shifts the value by the UTC offset and steps over every
      // row tied on that timestamp. updated_at is only a fallback for talking to
      // a backend that predates sync_cursor, where the old single-page behaviour
      // is the alternative anyway.
      const cursorValue = last?.sync_cursor || last?.updated_at;
      if (!cursorValue || last?.id === undefined || last?.id === null) {
        // Without a usable cursor another page would repeat this one forever.
        console.error(`Sync: ${table} page ${page} has no usable cursor; stopping short.`);
        return false;
      }
      cursor = { updated_at: cursorValue, id: last.id };
    }

    console.error(
      `Sync: ${table} still had more rows after ${SYNC_MAX_PAGES} pages ` +
      `(${SYNC_MAX_PAGES * SYNC_PAGE_SIZE} rows). Not advancing lastSync.`
    );
    return false;
  },

  async pullChanges(token) {
    const lastSync = await getLastSyncTime();
    const headers = { Authorization: `Bearer ${token}` };
    const syncParams = { updated_after: lastSync || '' };
    let pullFailed = false;

    // 1. Fetch Mice
    try {
      const drained = await this._drainTable({
        endpoint: '/api/mice', table: 'mice', headers, baseParams: syncParams,
      });
      // An undrained table is treated exactly like a failed one: lastSync must
      // not advance, or the rows we never got would never be asked for again.
      if (!drained) pullFailed = true;
    } catch (err) {
      console.error("Error pulling mice:", err);
      pullFailed = true;
    }

    // 2. Fetch Cages (incremental sync using updated_after / sync_mode)
    try {
        const drained = await this._drainTable({
          endpoint: '/api/cages', table: 'cages', headers, baseParams: syncParams,
        });
        if (!drained) pullFailed = true;
    } catch (err) {
        console.error("Error pulling cages:", err);
        pullFailed = true;
    }

    // 3. Fetch Strains/Locus/Allele (Static data — max once per 5 minutes, incremental delta)
    if (Date.now() - _lastStaticPull > STATIC_DATA_TTL_MS) {
      try {
          // Pass sync_mode + updated_after so the backend only returns records changed
          // since the last successful sync (these tables use updated_at as their delta
          // timestamp — deactivated rows sync down as tombstones).
          const staticParams = {
              sync_mode: 'true',
              updated_after: lastSync || '',
              _limit: MAX_LIST_LIMIT
          };
          const [strains, locus, allele] = await Promise.all([
              axios.get(`${API_URL}/api/strains`, { headers, params: staticParams }),
              axios.get(`${API_URL}/api/locus`, { headers, params: staticParams }),
              axios.get(`${API_URL}/api/allele`, { headers, params: staticParams })
          ]);
          await db.strains.bulkPut(strains.data.data);
          await db.locus.bulkPut(locus.data.data);
          await db.allele.bulkPut(allele.data.data);
          _lastStaticPull = Date.now();
      } catch (err) {
          console.error("Error pulling static data:", err);
          pullFailed = true;
      }
    }

    // 4. Fetch Litters (incremental sync, all statuses so archived litters are cached too)
    try {
        const drained = await this._drainTable({
          endpoint: '/api/litters', table: 'litters', headers,
          baseParams: { ...syncParams, filter_status: '' },
        });
        if (!drained) pullFailed = true;
    } catch (err) {
        console.error("Error pulling litters:", err);
        pullFailed = true;
    }

    // 5. Fetch Pregnancies (C5 — previously not synced at all)
    try {
        const drained = await this._drainTable({
          endpoint: '/api/pregnancies', table: 'pregnancies', headers, baseParams: syncParams,
        });
        if (!drained) pullFailed = true;
    } catch (err) {
        console.error("Error pulling pregnancies:", err);
        pullFailed = true;
    }

    // 6. Clean up orphaned temp records: temp_ IDs that have no pending CREATE
    //    in the sync queue or failedSync (their CREATE already synced — the real
    //    server record replaced them — but the old temp copy was never deleted).
    try {
        const pendingCreates = await db.syncQueue.filter(q => q.action === 'CREATE').toArray();
        const failedCreates = await db.failedSync.filter(q => q.action === 'CREATE').toArray();
        const pendingTempIds = new Set([
            ...pendingCreates.map(q => String(q.tempId)).filter(Boolean),
            ...failedCreates.map(q => String(q.tempId)).filter(Boolean),
        ]);
        for (const table of ['mice', 'cages', 'litters', 'pregnancies']) {
            const tempRecords = await db[table].filter(r => String(r.id).startsWith('temp_')).toArray();
            for (const r of tempRecords) {
                if (!pendingTempIds.has(String(r.id))) {
                    await db[table].delete(r.id);
                }
            }
        }
    } catch (err) {
        console.error('Error cleaning up orphaned temp records:', err);
    }

    // Only advance lastSync if all pulls succeeded; otherwise the failed
    // entities would be skipped on the next sync.
    if (!pullFailed) {
        await setLastSyncTime(new Date().toISOString());
    }
  },

  // --- CRUD Operations (Local First) ---

  async getMice(filters = {}) {
    // Start with status filter using index when possible
    let collection;
    if (filters.status && filters.status !== '') {
        collection = db.mice.where('status').equals(filters.status);
    } else {
        // No status filter = all mice
        collection = db.mice.toCollection();
    }

    // Apply remaining filters in-memory
    const items = await collection.filter(mouse => {
      // Free-text search across all fields
      if (filters.search && filters.search.trim()) {
        const terms = filters.search.trim().toLowerCase().split(',').map(t => t.trim()).filter(t => t);
        const matchesTerm = (term) =>
          (mouse.physical_tag && mouse.physical_tag.toLowerCase().includes(term)) ||
          (mouse.sex && mouse.sex.toLowerCase().includes(term)) ||
          (mouse.status && mouse.status.toLowerCase().includes(term)) ||
          (mouse.notes && mouse.notes.toLowerCase().includes(term)) ||
          (mouse.display_cage_number && mouse.display_cage_number.toLowerCase().includes(term)) ||
          (mouse.cage_location && mouse.cage_location.toLowerCase().includes(term)) ||
          (mouse.strain && mouse.strain.toLowerCase().includes(term)) ||
          (Array.isArray(mouse.genotype) && mouse.genotype.some(g =>
            (g.locus_name && g.locus_name.toLowerCase().includes(term)) ||
            (g.allele_name && g.allele_name.toLowerCase().includes(term))
          ));
        if (!terms.every(matchesTerm)) return false;
      }

      // Field-specific search filters (from prefix syntax, e.g. cage:Blue-42)
      const ff = filters.fieldFilters || {};
      if (ff.cage) {
        const t = ff.cage.toLowerCase();
        if (!(mouse.display_cage_number && mouse.display_cage_number.toLowerCase().includes(t))) return false;
      }
      if (ff.mouse) {
        const t = ff.mouse.toLowerCase();
        if (!(mouse.physical_tag && mouse.physical_tag.toLowerCase().includes(t))) return false;
      }
      if (ff.strain) {
        const t = ff.strain.toLowerCase();
        if (!(mouse.strain && mouse.strain.toLowerCase().includes(t))) return false;
      }
      if (ff.location) {
        const t = ff.location.toLowerCase();
        if (!(mouse.cage_location && mouse.cage_location.toLowerCase().includes(t))) return false;
      }
      if (ff.sex) {
        const t = ff.sex.toLowerCase();
        if (!(mouse.sex && mouse.sex.toLowerCase().includes(t))) return false;
      }
      if (ff.notes) {
        const t = ff.notes.toLowerCase();
        if (!(mouse.notes && mouse.notes.toLowerCase().includes(t))) return false;
      }
      if (ff.locus) {
        const t = ff.locus.toLowerCase();
        if (!(Array.isArray(mouse.genotype) && mouse.genotype.some(g => g.locus_name && g.locus_name.toLowerCase().includes(t)))) return false;
      }
      if (ff.allele) {
        const t = ff.allele.toLowerCase();
        if (!(Array.isArray(mouse.genotype) && mouse.genotype.some(g => g.allele_name && g.allele_name.toLowerCase().includes(t)))) return false;
      }

      // Cage filter
      if (filters.filter_cage_id && String(mouse.cage_id) !== String(filters.filter_cage_id)) return false;

      // Sex filter
      if (filters.filter_sex && mouse.sex !== filters.filter_sex) return false;

      // Age filter (min_age_weeks / max_age_weeks)
      if ((filters.min_age_weeks || filters.max_age_weeks) && mouse.date_of_birth) {
        const dob = new Date(mouse.date_of_birth);
        const ageWeeks = (Date.now() - dob.getTime()) / (7 * 24 * 60 * 60 * 1000);
        if (filters.min_age_weeks && ageWeeks < parseFloat(filters.min_age_weeks)) return false;
        if (filters.max_age_weeks && ageWeeks > parseFloat(filters.max_age_weeks)) return false;
      }

      // Genotype locus/allele filter
      if (filters.filter_genotype_locus || filters.filter_genotype_allele) {
        if (!Array.isArray(mouse.genotype) || mouse.genotype.length === 0) return false;
        const matchesLocus = !filters.filter_genotype_locus ||
          mouse.genotype.some(g => g.locus_name && g.locus_name.toLowerCase() === filters.filter_genotype_locus.toLowerCase());
        const matchesAllele = !filters.filter_genotype_allele ||
          mouse.genotype.some(g => g.allele_name && g.allele_name.toLowerCase() === filters.filter_genotype_allele.toLowerCase());
        if (!matchesLocus || !matchesAllele) return false;
      }

      return true;
    }).toArray();

    // Sort in memory
    const sortCol = filters.sortColumn || 'created_at';
    const sortAsc = (filters.sortOrder || 'desc') === 'asc';
    items.sort((a, b) => {
      const valA = a[sortCol] ?? '';
      const valB = b[sortCol] ?? '';
      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });

    const totalCount = items.length;

    // Apply pagination
    const page = parseInt(filters.page) || 1;
    const limit = parseInt(filters.limit) || 10;
    const offset = (page - 1) * limit;
    const paginated = items.slice(offset, offset + limit);

    return { data: paginated, totalCount };
  },

  // Update a cage's mice_in_cage array and mouse_count in local Dexie
  // so that cage views stay consistent after mouse create/move/archive.
  async _updateLocalCageCounts(cageId, mouseRecord, action) {
    if (!cageId) return;
    const cage = await db.cages.get(cageId);
    if (!cage) return;
    const list = cage.mice_in_cage || [];
    if (action === 'remove') {
      cage.mice_in_cage = list.filter(m => String(m.id || m) !== String(mouseRecord.id));
      cage.mouse_count = Math.max(0, (Number(cage.mouse_count) || 1) - 1);
    } else if (action === 'add') {
      if (!list.some(m => String(m.id || m) === String(mouseRecord.id))) {
        cage.mice_in_cage = [...list, { id: mouseRecord.id, physical_tag: mouseRecord.physical_tag, sex: mouseRecord.sex }];
        cage.mouse_count = (Number(cage.mouse_count) || 0) + 1;
      }
    }
    cage.updated_at = new Date().toISOString();
    await db.cages.put(cage);
  },

  async saveMouse(mouseData) {
    if (!mouseData.id || mouseData.id.toString().startsWith('temp_')) {
       const tempId = mouseData.id || `temp_${crypto.randomUUID()}`;

       const pendingCreate = await db.syncQueue.where('tempId').equals(String(tempId)).first();
       if (pendingCreate) {
           const existing = await db.mice.get(tempId) || {};
           const merged = { ...existing, ...mouseData, id: tempId, updated_at: new Date().toISOString() };
           if (mouseData.genotype_data !== undefined) merged.genotype = mouseData.genotype_data;
           await db.mice.put(merged);
           await db.syncQueue.update(pendingCreate.id, { payload: merged, timestamp: Date.now() });
       } else {
           const newMouse = { ...mouseData, id: tempId, status: 'Active', updated_at: new Date().toISOString() };
           if (mouseData.genotype_data !== undefined) newMouse.genotype = mouseData.genotype_data;
           await db.mice.put(newMouse);
           if (newMouse.cage_id) {
               await this._updateLocalCageCounts(newMouse.cage_id, newMouse, 'add');
           }
           await db.syncQueue.add({
               action: 'CREATE',
               endpoint: '/api/mice',
               table: 'mice',
               payload: newMouse,
               tempId,
               timestamp: Date.now(),
           });
       }
    } else {
       const existing = await db.mice.get(mouseData.id) || {};
       const merged = { ...existing, ...mouseData, updated_at: new Date().toISOString() };
       if (mouseData.genotype_data !== undefined) merged.genotype = mouseData.genotype_data;
       await db.mice.put(merged);
       // Update local cage counts when cage_id changes
       if (mouseData.cage_id !== undefined && mouseData.cage_id !== existing.cage_id) {
           await this._updateLocalCageCounts(existing.cage_id, existing, 'remove');
           await this._updateLocalCageCounts(mouseData.cage_id, merged, 'add');
       }
       await db.syncQueue.add({
           action: 'UPDATE',
           endpoint: `/api/mice/${mouseData.id}`,
           table: 'mice',
           payload: merged,
           timestamp: Date.now(),
       });
    }

    if (isOnline()) this.sync();
  },

  async bulkMoveMice(miceIds, newCageId) {
    const movedMice = [];
    for (const mouseId of miceIds) {
      const existing = await db.mice.get(mouseId);
      if (existing) {
        await this._updateLocalCageCounts(existing.cage_id, existing, 'remove');
        const updated = { ...existing, cage_id: newCageId, updated_at: new Date().toISOString() };
        await db.mice.put(updated);
        movedMice.push(updated);
      }
    }
    for (const mouse of movedMice) {
      await this._updateLocalCageCounts(newCageId, mouse, 'add');
    }
    await db.syncQueue.add({
      action: 'BULK_MOVE',
      endpoint: '/api/mice/bulk-move',
      payload: { mice_to_move: miceIds.map(id => ({ id, cage_id: newCageId })) },
      timestamp: Date.now(),
    });
    if (isOnline()) this.sync();
  },

  async archiveMouse(id) {
      const mouse = await db.mice.get(id);
      if (mouse) {
          if (String(id).startsWith('temp_')) {
              // Temp record never reached the server — remove locally and drop its pending CREATE
              await this._updateLocalCageCounts(mouse.cage_id, mouse, 'remove');
              await db.mice.delete(id);
              const pending = await db.syncQueue.where('tempId').equals(String(id)).first();
              if (pending) await db.syncQueue.delete(pending.id);
          } else {
              mouse.status = 'Archived';
              mouse.updated_at = new Date().toISOString();
              await db.mice.put(mouse);
              await this._updateLocalCageCounts(mouse.cage_id, mouse, 'remove');
              await db.syncQueue.add({
                  action: 'DELETE',
                  endpoint: `/api/mice/${id}`,
                  payload: {},
                  timestamp: Date.now()
              });
              if (isOnline()) this.sync();
          }
      }
  },

  // C11: the local-first counterpart to archiveMouse, so Undo works offline.
  // The Restore button in the archived-mice view still calls the API directly;
  // this exists for the undo path, where the archive itself may only be queued.
  async restoreMouse(id) {
      const mouse = await db.mice.get(id);
      if (!mouse) return;

      mouse.status = 'Active';
      mouse.updated_at = new Date().toISOString();
      await db.mice.put(mouse);
      await this._updateLocalCageCounts(mouse.cage_id, mouse, 'add');

      // If the archive has not been pushed yet, drop it rather than queueing a
      // restore behind it — archiving and then un-archiving the same record is
      // a round trip the server never needs to hear about.
      const queuedArchive = await db.syncQueue
          .filter(q => q.action === 'DELETE' && q.endpoint === `/api/mice/${id}`)
          .first();

      if (queuedArchive) {
          await db.syncQueue.delete(queuedArchive.id);
      } else {
          await db.syncQueue.add({
              action: 'UPDATE',
              endpoint: `/api/mice/${id}/restore`,
              table: 'mice',
              payload: {},
              timestamp: Date.now(),
          });
      }

      if (isOnline()) this.sync();
      return mouse;
  },

  async getCages() {
      return await db.cages.where('status').equals('Active').toArray();
  },

  async getStrains() {
      return await db.strains.toArray();
  },

  async getLocus() {
      const rows = await db.locus.toArray();
      // Hide deactivated rows from pickers. `!== false` (not `=== true`) so
      // pre-migration cached rows without the field stay visible.
      return rows.filter(x => x.is_active !== false);
  },

  async getAllele() {
      const rows = await db.allele.toArray();
      // Hide deactivated rows from pickers. `!== false` (not `=== true`) so
      // pre-migration cached rows without the field stay visible.
      return rows.filter(x => x.is_active !== false);
  },

  async getCagesAll() {
      return await db.cages.toArray();
  },

  async saveCage(cageData) {
      let savedCage;
      if (!cageData.id || String(cageData.id).startsWith('temp_')) {
          const tempId = cageData.id || `temp_${crypto.randomUUID()}`;

          const pendingCreate = await db.syncQueue.where('tempId').equals(String(tempId)).first();
          if (pendingCreate) {
              const existing = await db.cages.get(tempId) || {};
              savedCage = { ...existing, ...cageData, id: tempId, updated_at: new Date().toISOString() };
              await db.cages.put(savedCage);
              await db.syncQueue.update(pendingCreate.id, { payload: savedCage, timestamp: Date.now() });
          } else {
              const now = new Date().toISOString();
              savedCage = { ...cageData, id: tempId, status: 'Active', created_at: now, updated_at: now };
              await db.cages.put(savedCage);
              await db.syncQueue.add({
                  action: 'CREATE',
                  endpoint: '/api/cages',
                  table: 'cages',
                  payload: savedCage,
                  tempId,
                  timestamp: Date.now(),
              });
          }
      } else {
          const existing = await db.cages.get(cageData.id) || {};
          savedCage = { ...existing, ...cageData, updated_at: new Date().toISOString() };
          await db.cages.put(savedCage);
          // NOTE: The local Dexie record stores 'breeding_status' (to distinguish it from
          // the cage's active/archived 'status' field). The backend PUT /api/cages/:id
          // reads 'status' from the request body and maps it to the breeding_status column
          // in Postgres. Do NOT rename this key or the two sides will diverge.
          const { breeding_status, id: _id, ...restPayload } = savedCage;
          const serverPayload = {
              ...restPayload,
              ...(breeding_status !== undefined && { status: breeding_status }),
          };
          await db.syncQueue.add({
              action: 'UPDATE',
              endpoint: `/api/cages/${cageData.id}`,
              table: 'cages',
              payload: serverPayload,
              timestamp: Date.now(),
          });
      }
      if (isOnline()) this.sync();
      return savedCage;
  },

  // Store a server-returned cage in the local cache without queuing a sync action.
  // Used after axios.post in the UI so weanLitter can resolve display_cage_number.
  async putCageToCache(cage) {
      await db.cages.put({ ...cage, updated_at: cage.updated_at || new Date().toISOString() });
  },

  async archiveCage(id) {
      const cage = await db.cages.get(id);
      if (cage) {
          if (String(id).startsWith('temp_')) {
              await db.cages.delete(id);
              const pending = await db.syncQueue.where('tempId').equals(String(id)).first();
              if (pending) await db.syncQueue.delete(pending.id);
          } else {
              cage.status = 'Archived';
              cage.updated_at = new Date().toISOString();
              await db.cages.put(cage);
              await db.syncQueue.add({
                  action: 'DELETE',
                  endpoint: `/api/cages/${id}`,
                  table: 'cages',
                  payload: {},
                  timestamp: Date.now(),
              });
              if (isOnline()) this.sync();
          }
      }
  },

  async saveLitter(litterData) {
      // Fields computed by backend JOINs — stored locally but never sent back to the server
      const JOINED_FIELDS = ['mother_genotype', 'father_genotype', 'mother_mouse_id', 'father_mouse_id',
                             'display_cage_number', 'current_live_pups', 'owner_initials'];

      // Always store pup_records as an array in IndexedDB; serialize to JSON string for the server
      const normalizePupRecords = (val) => {
          if (!val) return [];
          if (typeof val === 'string') { try { return JSON.parse(val); } catch { return []; } }
          return val;
      };
      const serializeForServer = (payload) => {
          const p = { ...payload };
          if (Array.isArray(p.pup_records)) p.pup_records = JSON.stringify(p.pup_records);
          return p;
      };

      const tempId = litterData.id && String(litterData.id).startsWith('temp_') ? litterData.id : null;

      if (!litterData.id || tempId) {
          const id = litterData.id || `temp_${crypto.randomUUID()}`;

          // Check if a CREATE is already queued for this temp record.
          // If so, the user is editing an unsynced record — update the pending CREATE payload
          // instead of queuing a second CREATE (which would duplicate on the server).
          const pendingCreate = await db.syncQueue.where('tempId').equals(String(id)).first();

          if (pendingCreate) {
              // Edit of an unsynced record: merge changes into the local copy and patch the queue
              const existing = await db.litters.get(id) || {};
              const merged = {
                  ...existing,
                  ...litterData,
                  id,
                  pup_records: normalizePupRecords(litterData.pup_records ?? existing.pup_records),
                  updated_at: new Date().toISOString(),
              };
              await db.litters.put(merged);
              const serverPayload = serializeForServer(
                  Object.fromEntries(Object.entries(merged).filter(([k]) => !JOINED_FIELDS.includes(k)))
              );
              await db.syncQueue.update(pendingCreate.id, { payload: serverPayload, timestamp: Date.now() });

          } else {
              // First save of this record — resolve denormalized fields and create
              const [mother, father, cage] = await Promise.all([
                  litterData.mother_id ? db.mice.get(litterData.mother_id) : null,
                  litterData.father_id ? db.mice.get(litterData.father_id) : null,
                  litterData.cage_id   ? db.cages.get(litterData.cage_id)  : null,
              ]);

              const existingPupRecords = normalizePupRecords(litterData.pup_records);
              const count = parseInt(litterData.initial_pup_count, 10) || 0;
              const pupGenotypeTemplate = computePupGenotype(mother?.genotype, father?.genotype);
              const initialPupRecords = existingPupRecords.length > 0
                  ? existingPupRecords
                  : Array.from({ length: count }, () => ({
                        id: crypto.randomUUID(),
                        sex: 'Unassigned',
                        physical_tag: '',
                        genotype_data: pupGenotypeTemplate,
                        strain_id: litterData.strain_id || null,
                        weaned: false,
                    }));

              const newLitter = {
                  ...litterData,
                  id,
                  status: 'Born',
                  updated_at: new Date().toISOString(),
                  pup_records: initialPupRecords,
                  weaned_pup_count: 0,
                  all_pups_tagged: false,
                  current_live_pups: count,
                  mother_genotype:     mother?.genotype           || [],
                  father_genotype:     father?.genotype           || [],
                  mother_mouse_id:     mother?.mouse_id           || null,
                  father_mouse_id:     father?.mouse_id           || null,
                  display_cage_number: cage?.display_cage_number  || null,
              };

              await db.litters.put(newLitter);
              const serverPayload = serializeForServer(
                  Object.fromEntries(Object.entries(newLitter).filter(([k]) => !JOINED_FIELDS.includes(k)))
              );
              await db.syncQueue.add({
                  action: 'CREATE',
                  endpoint: '/api/litters',
                  table: 'litters',
                  payload: serverPayload,
                  tempId: id,
                  timestamp: Date.now(),
              });
          }

      } else {
          // Real server ID — merge and queue UPDATE
          const existing = await db.litters.get(litterData.id) || {};
          const merged = {
              ...existing,
              ...litterData,
              pup_records: normalizePupRecords(litterData.pup_records ?? existing.pup_records),
              updated_at: new Date().toISOString(),
          };
          await db.litters.put(merged);
          const serverPayload = serializeForServer(
              Object.fromEntries(Object.entries(merged).filter(([k]) => !JOINED_FIELDS.includes(k)))
          );
          await db.syncQueue.add({
              action: 'UPDATE',
              endpoint: `/api/litters/${litterData.id}`,
              table: 'litters',
              payload: serverPayload,
              timestamp: Date.now(),
          });
      }
      if (isOnline()) this.sync();
  },

  // --- Pregnancies (C5) ---
  //
  // These three were the last direct-to-axios calls in an otherwise offline-first
  // service: no Dexie table, no queue, no delta pull. Offline, the cage
  // pregnancy modals simply threw and showed nothing, which in a local-first UI
  // reads as "there is no pregnancy" rather than "we could not reach the
  // server". They now follow the same temp-id + syncQueue pattern as mice,
  // cages and litters.

  async createPregnancy(pregnancyData) {
      const tempId = `temp_${crypto.randomUUID()}`;

      // expected_due_date is computed server-side (breeding start + gestation).
      // Predicting it locally would risk disagreeing with the server, so the
      // field stays absent until the real record arrives; the UI already
      // tolerates a missing due date.
      const newPregnancy = {
          ...pregnancyData,
          id: tempId,
          status: 'Active',
          pregnancy_confirmed: pregnancyData.pregnancy_confirmed ?? false,
          updated_at: new Date().toISOString(),
      };

      await db.pregnancies.put(newPregnancy);
      await db.syncQueue.add({
          action: 'CREATE',
          endpoint: '/api/pregnancies',
          table: 'pregnancies',
          payload: newPregnancy,
          tempId,
          timestamp: Date.now(),
      });

      if (isOnline()) this.sync();
      return newPregnancy;
  },

  async getPregnanciesByCage(cageId) {
      // Read locally first so the modal opens with data offline. The background
      // refresh below is the same pattern the Dashboard uses.
      // String-compare the cage id: Postgres serialises bigint as a string, so a
      // synced row carries cage_id "420" while a locally-created one carries the
      // number the component held. Dexie's equals() is type-strict, so an index
      // lookup would silently miss half of them. The table is small enough that
      // a filter costs nothing.
      const key = String(cageId);
      const local = await db.pregnancies
          .filter(p => String(p.cage_id) === key && p.status === 'Active')
          .toArray();

      if (isOnline()) {
          try {
              const token = getToken();
              const response = await axios.get(
                  `${API_URL}/api/pregnancies?filter_cage_id=${cageId}&filter_status=Active&_limit=100`,
                  { headers: { Authorization: `Bearer ${token}` } }
              );
              const rows = response.data || [];
              await db.pregnancies.bulkPut(rows.map(({ sync_cursor, ...r }) => r));

              // Keep any temp record the server has not seen yet — dropping it
              // would make a just-created pregnancy vanish mid-sync.
              const pending = local.filter(p => String(p.id).startsWith('temp_'));
              const serverIds = new Set(rows.map(r => String(r.id)));
              return [...rows, ...pending.filter(p => !serverIds.has(String(p.id)))];
          } catch (err) {
              console.error('Pregnancy refresh failed, using local cache:', err);
          }
      }

      return local;
  },

  async updatePregnancy(pregnancyId, updateData) {
      const existing = await db.pregnancies.get(pregnancyId) || {};
      const merged = { ...existing, ...updateData, id: pregnancyId, updated_at: new Date().toISOString() };
      await db.pregnancies.put(merged);

      if (String(pregnancyId).startsWith('temp_')) {
          // The CREATE has not resolved yet. Fold the edit into the queued
          // payload rather than queueing an UPDATE against an id the server has
          // never issued — the same reasoning as saveMouse's pendingCreate path.
          const pendingCreate = await db.syncQueue.where('tempId').equals(String(pregnancyId)).first();
          if (pendingCreate) {
              await db.syncQueue.update(pendingCreate.id, { payload: merged, timestamp: Date.now() });
              if (isOnline()) this.sync();
              return merged;
          }
      }

      await db.syncQueue.add({
          action: 'UPDATE',
          endpoint: `/api/pregnancies/${pregnancyId}`,
          table: 'pregnancies',
          payload: merged,
          timestamp: Date.now(),
      });

      if (isOnline()) this.sync();
      return merged;
  },

  async weanLitter(litterId, weanedMiceData) {
      // 1. Create temp mouse records locally so they appear immediately in the Mice list
      const tempMiceIds = [];
      // Track new mice per cage so we can update cage.mice_in_cage for the offline cage view
      const newMiceByCage = {};

      for (const mouseData of weanedMiceData) {
          const tempId = `temp_${crypto.randomUUID()}`;
          const cage = mouseData.cage_id ? await db.cages.get(mouseData.cage_id) : null;
          const tempMouse = {
              id: tempId,
              physical_tag: mouseData.physical_tag || '',
              sex: mouseData.sex === 'Male' ? 'M' : mouseData.sex === 'Female' ? 'F' : (mouseData.sex || 'Unassigned'),
              status: 'Active',
              cage_id: mouseData.cage_id || null,
              date_of_birth: mouseData.date_of_birth || null,
              litter_id: litterId,
              strain_id: mouseData.strain_id || null,
              notes: mouseData.notes || '',
              genotype: mouseData.genotype_data || [],
              display_cage_number: cage?.display_cage_number || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
          };
          await db.mice.put(tempMouse);
          tempMiceIds.push(tempId);

          if (mouseData.cage_id) {
              if (!newMiceByCage[mouseData.cage_id]) newMiceByCage[mouseData.cage_id] = [];
              // Store the shape CageManagement uses for mice_in_cage display
              newMiceByCage[mouseData.cage_id].push({
                  id: tempId,
                  physical_tag: tempMouse.physical_tag,
                  sex: tempMouse.sex,
                  genotype: tempMouse.genotype,
                  date_of_birth: tempMouse.date_of_birth,
              });
          }
      }

      // Update each destination cage's embedded mice list so the cage view shows them offline
      for (const [cageId, newMice] of Object.entries(newMiceByCage)) {
          const cage = await db.cages.get(cageId);
          if (cage) {
              await db.cages.put({
                  ...cage,
                  mice_in_cage: [...(cage.mice_in_cage || []), ...newMice],
                  mouse_count: (cage.mouse_count || 0) + newMice.length,
                  updated_at: new Date().toISOString(),
              });
          }
      }

      // 2. Update the litter locally: mark pups as weaned, update counts & status
      const litter = await db.litters.get(litterId);
      if (litter) {
          const currentPupRecords = Array.isArray(litter.pup_records) ? [...litter.pup_records] : [];
          for (const mouseData of weanedMiceData) {
              const idx = currentPupRecords.findIndex(p => p.id === mouseData.pup_temp_id);
              if (idx !== -1) currentPupRecords[idx] = { ...currentPupRecords[idx], weaned: true };
          }
          const newWeanedCount = (litter.weaned_pup_count || 0) + weanedMiceData.length;
          const newStatus = newWeanedCount >= (litter.initial_pup_count || 0) ? 'Archived' : 'Born';
          await db.litters.put({
              ...litter,
              pup_records: currentPupRecords,
              weaned_pup_count: newWeanedCount,
              status: newStatus,
              updated_at: new Date().toISOString(),
          });
      }

      // 3. Queue the WEAN action — the server executes it atomically on sync
      await db.syncQueue.add({
          action: 'WEAN',
          endpoint: `/api/litters/${litterId}/wean-pups`,
          table: 'litters',
          payload: { weaned_mice_data: weanedMiceData },
          tempMiceIds,
          timestamp: Date.now(),
      });

      if (isOnline()) this.sync();
  },

  async archiveLitter(id) {
      const litter = await db.litters.get(id);
      if (litter) {
          if (String(id).startsWith('temp_')) {
              await db.litters.delete(id);
              const pending = await db.syncQueue.where('tempId').equals(String(id)).first();
              if (pending) await db.syncQueue.delete(pending.id);
          } else {
              litter.status = 'Archived';
              litter.updated_at = new Date().toISOString();
              await db.litters.put(litter);
              await db.syncQueue.add({
                  action: 'DELETE',
                  endpoint: `/api/litters/${id}`,
                  table: 'litters',
                  payload: {},
                  timestamp: Date.now(),
              });
              if (isOnline()) this.sync();
          }
      }
  },

  async clearAllData() {
    _lastStaticPull = 0;
    // Single transaction so the clear is all-or-nothing — nine parallel
    // transactions could leave a partially-cleared cache if interrupted.
    await db.transaction('rw', db.tables, async () => {
      await Promise.all(db.tables.map(table => table.clear()));
    });
  },

  // Rebuild the local cache after the user's RLS scope changes (C28/M7).
  //
  // Archives are soft deletes and sync down fine, but a row that LEAVES the
  // user's scope — leaving a lab, being removed from one, a lab→personal
  // switch — simply stops appearing in the delta. Nothing tells the client it
  // is gone, so the stale copy lives in Dexie indefinitely and the user keeps
  // seeing a colony they no longer have access to. ensureCacheOwner covers
  // account switches, not membership changes.
  //
  // Clearing lastSync alone is not enough: a full re-pull returns the rows that
  // ARE in scope and says nothing about the ones that left, so the stale copies
  // would survive it. The cached tables have to be emptied and refilled.
  //
  // syncQueue and failedSync are deliberately preserved — they hold work the
  // user did that the server has not accepted yet, and losing it would be
  // real data loss. Pending work is pushed first, since every lab transition
  // happens online (it is an API call), so there is no offline case here.
  async resetCacheForScopeChange() {
    const token = getToken();

    if (token) {
      try {
        await this.pushChanges(token);
      } catch (err) {
        // Don't block the scope change on a push failure — the queue survives
        // the clear below and will be retried by the next sync.
        console.error('Scope change: failed to push pending changes first:', err);
      }
    }

    // Only the tables that are filled by pullChanges. kv is spared so the
    // dbOwner stamp survives; lastSync is cleared explicitly.
    const CACHED_TABLES = ['mice', 'cages', 'litters', 'pregnancies', 'strains', 'locus', 'allele'];
    _lastStaticPull = 0;
    await db.transaction('rw', CACHED_TABLES.map(t => db[t]), async () => {
      await Promise.all(CACHED_TABLES.map(t => db[t].clear()));
    });
    await db.kv.delete('lastSync');

    if (token) {
      await this.pullChanges(token);
      window.dispatchEvent(new CustomEvent('squeakbase-sync-complete'));
    }
  },

  // Ensure the local cache belongs to the given user. If it was populated by a
  // different account (e.g. a logout whose clear never completed, or WKWebView
  // wiping localStorage but not IndexedDB), wipe it — clearing kv also resets
  // lastSync, forcing a full re-pull for the new account. A cache with no
  // ownership stamp (pre-stamp installs) is only adopted as-is when it holds
  // unsynced local mutations we must not destroy; otherwise it starts clean.
  async ensureCacheOwner(userId) {
    const stamp = await db.kv.get('dbOwner');
    if (stamp && String(stamp.value) === String(userId)) return;
    if (stamp) {
      await this.clearAllData();
    } else {
      const [pendingCount, failedCount] = await Promise.all([
        db.syncQueue.count(),
        db.failedSync.count(),
      ]);
      if (pendingCount === 0 && failedCount === 0) {
        await this.clearAllData();
      }
    }
    await db.kv.put({ key: 'dbOwner', value: userId });
  },

  async getFailedSyncItems() {
    return await db.failedSync.toArray();
  },

  // C13: retry every failed item in one go. The banner previously offered only
  // "Review", which meant navigating to Settings and clicking retry once per
  // row — and failures usually arrive in batches (one lost connection during a
  // push fails everything queued behind it), so the per-item loop was exactly
  // the wrong shape for the common case.
  //
  // Requeues in the order the items originally failed, because a CREATE and the
  // UPDATE that depends on it must not swap places.
  async retryAllFailedSyncItems() {
    const items = await db.failedSync.orderBy('timestamp').toArray();
    if (items.length === 0) return { requeued: 0 };

    for (const item of items) {
      await db.syncQueue.add({
        action: item.action,
        endpoint: item.endpoint,
        table: item.table,
        payload: item.payload,
        tempId: item.tempId,
        tempMiceIds: item.tempMiceIds,
        timestamp: Date.now(),
        retryCount: 0,
      });
      await db.failedSync.delete(item.id);
    }

    if (isOnline()) await this.sync();
    return { requeued: items.length };
  },

  async retryFailedSyncItem(failedItemId) {
    const item = await db.failedSync.get(failedItemId);
    if (!item) return;
    await db.syncQueue.add({
      action: item.action,
      endpoint: item.endpoint,
      table: item.table,
      payload: item.payload,
      tempId: item.tempId,
      tempMiceIds: item.tempMiceIds,
      timestamp: Date.now(),
      retryCount: 0,
    });
    await db.failedSync.delete(failedItemId);
    if (isOnline()) this.sync();
  },

  async dismissFailedSyncItem(failedItemId) {
    const item = await db.failedSync.get(failedItemId);
    if (!item) return;
    await db.failedSync.delete(failedItemId);
    // Clean up the orphaned temp record
    if (item.action === 'CREATE' && item.tempId) {
      await db[item.table || 'mice'].delete(item.tempId).catch(() => {});
    }
    if (item.action === 'WEAN' && item.tempMiceIds) {
      await Promise.all(item.tempMiceIds.map(id => db.mice.delete(id).catch(() => {})));
    }
  },

  async getLitters(filters = {}) {
      let collection;
      // Litter statuses are 'Born', 'Weaned', 'Dispersed', 'Archived' — never 'Active'.
      // 'Active' is a frontend convention meaning "not archived" (matching backend behavior).
      if (filters.status && filters.status !== '' && filters.status !== 'Active') {
          collection = db.litters.where('status').equals(filters.status);
      } else {
          collection = db.litters.toCollection();
      }

      const items = await collection.filter(litter => {
          // 'Active' filter = exclude archived (mirrors backend: filter_status='Active' → status != 'Archived')
          if (filters.status === 'Active' && litter.status === 'Archived') return false;

          if (filters.search && filters.search.trim()) {
              const terms = filters.search.trim().toLowerCase().split(',').map(t => t.trim()).filter(t => t);
              const matchesTerm = (term) =>
                  (litter.display_cage_number && litter.display_cage_number.toLowerCase().includes(term)) ||
                  (litter.notes && litter.notes.toLowerCase().includes(term)) ||
                  (litter.litter_number && litter.litter_number.toLowerCase().includes(term)) ||
                  (litter.status && litter.status.toLowerCase().includes(term)) ||
                  (litter.date_of_birth && litter.date_of_birth.toLowerCase().includes(term)) ||
                  (litter.mother_mouse_id && litter.mother_mouse_id.toLowerCase().includes(term)) ||
                  (litter.father_mouse_id && litter.father_mouse_id.toLowerCase().includes(term)) ||
                  (String(litter.initial_pup_count ?? '').includes(term)) ||
                  (String(litter.weaned_pup_count ?? '').includes(term)) ||
                  (Array.isArray(litter.mother_genotype) && litter.mother_genotype.some(g =>
                    (g.locus_name && g.locus_name.toLowerCase().includes(term)) ||
                    (g.allele_name && g.allele_name.toLowerCase().includes(term))
                  )) ||
                  (Array.isArray(litter.father_genotype) && litter.father_genotype.some(g =>
                    (g.locus_name && g.locus_name.toLowerCase().includes(term)) ||
                    (g.allele_name && g.allele_name.toLowerCase().includes(term))
                  ));
              if (!terms.every(matchesTerm)) return false;
          }
          return true;
      }).toArray();

      // Sort by requested column or default to date_of_birth DESC
      const sortCol = filters.sortColumn || 'date_of_birth';
      const sortAsc = (filters.sortOrder || 'desc') === 'asc';
      items.sort((a, b) => {
          const valA = a[sortCol] ?? a.updated_at ?? '';
          const valB = b[sortCol] ?? b.updated_at ?? '';
          if (valA < valB) return sortAsc ? -1 : 1;
          if (valA > valB) return sortAsc ? 1 : -1;
          return 0;
      });

      const totalCount = items.length;
      const page = parseInt(filters.page) || 1;
      const limit = parseInt(filters.limit) || 10;
      const offset = (page - 1) * limit;
      return { data: items.slice(offset, offset + limit), totalCount };
  }
};
